import React from 'react'

/**
 * Label + optional help text wrapper for a single form field.
 *
 * Usage:
 *   <FormField label="Header title" help="Shown above every chat">
 *     <TextInput value={title} onChange={e => setTitle(e.target.value)} />
 *   </FormField>
 *
 * The field control (TextInput / TextArea / Select / etc.) is the only child.
 * FormField only handles the label + description rhythm; the control keeps
 * its own focus / disabled / error styling.
 *
 * `size` controls label prominence — "md" (default) is used on main forms,
 * "sm" is used inside section cards for compact sub-fields.
 */
export interface FormFieldProps {
  label: string
  help?: string
  size?: 'sm' | 'md'
  className?: string
  children: React.ReactNode
}

export function FormField({ label, help, size = 'md', className = '', children }: FormFieldProps) {
  const labelCls =
    size === 'md'
      ? 'block text-sm font-medium text-txt-primary mb-1.5'
      : 'block text-xs font-medium text-txt-secondary mb-1'
  return (
    <div className={className}>
      <label className={labelCls}>{label}</label>
      {children}
      {help && <p className="text-xs text-txt-tertiary mt-1">{help}</p>}
    </div>
  )
}
